/**
 * Admin Top Navigation Component
 * Top navigation bar with hamburger menu (mobile) and user menu
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

export default function TopNav({ onMenuClick }) {
  const { user, currentTenant, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Check if we're in impersonation mode
  const impersonating = localStorage.getItem('impersonationActive') === 'true';
  const impersonatedTenant = localStorage.getItem('impersonatedTenantName');

  const handleExitImpersonation = () => {
    // Restore original superadmin token
    const originalToken = localStorage.getItem('superadminToken');
    const originalRefresh = localStorage.getItem('superadminRefreshToken');
    if (originalToken) {
      localStorage.setItem('accessToken', originalToken);
      localStorage.setItem('refreshToken', originalRefresh || '');
    }
    localStorage.removeItem('impersonationActive');
    localStorage.removeItem('impersonatedTenantName');
    localStorage.removeItem('superadminToken');
    localStorage.removeItem('superadminRefreshToken');
    navigate('/superadmin/tenants');
    window.location.reload();
  };

  return (
    <>
      {/* Impersonation Banner */}
      {impersonating && (
        <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <ShieldCheckIcon className="w-4 h-4" />
            <span>Viewing as: <strong>{impersonatedTenant}</strong></span>
          </div>
          <button
            onClick={handleExitImpersonation}
            className="underline hover:no-underline font-medium"
          >
            Exit Impersonation
          </button>
        </div>
      )}

      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left: Hamburger + Title */}
          <div className="flex items-center">
            <button
              className="lg:hidden mr-4 text-gray-500 hover:text-gray-700 focus:outline-none"
              onClick={onMenuClick}
              aria-label="Open menu"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate max-w-[180px] sm:max-w-none">
                {currentTenant?.businessName || 'Admin Dashboard'}
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">
                Manage your massage studio
              </p>
            </div>
          </div>

          {/* Right: User Menu */}
          <div className="flex items-center space-x-4">
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <UserCircleIcon className="w-8 h-8" />
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-medium">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="text-xs text-gray-500">{user?.email}</div>
                </div>
              </button>

              {showUserMenu && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowUserMenu(false)}
                  />

                  {/* Dropdown */}
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>

                    {user?.isSuperadmin && (
                      <button
                        onClick={() => { setShowUserMenu(false); navigate('/superadmin/tenants'); }}
                        className="w-full text-left px-4 py-2 text-sm text-purple-700 hover:bg-purple-50 flex items-center"
                      >
                        <ShieldCheckIcon className="w-4 h-4 mr-2" />
                        Superadmin Panel
                      </button>
                    )}

                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                      Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
